import { useCallback, useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { DeleteIcon } from 'lucide-react';

import { Columns, VariantComment, Rows, processOutputData } from '../types';
import VariantDetailsPage from '../VariantDetailsPage';
import { useVariantDrawer } from '../hooks/useVariantDrawer';

interface InterpretationPageProps {
  outputID: string;
  allowValidateVariant: boolean;
  allowRejectVariant: boolean;
  personStatus: string;
}

const InterpretationPage = ({
  outputID,
  allowValidateVariant,
  allowRejectVariant,
  personStatus,
}: InterpretationPageProps) => {
  const [currentCursor, setCurrentCursor] = useState<string | null>(null);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [rowData, setRowData] = useState<Rows[]>([]);
  const [columns, setColumns] = useState<Columns[]>([]);
  const [rowCount, setRowCount] = useState(0);

  console.log(outputID, personStatus, currentCursor);

  const {
    drawerHeight,
    setDrawerHeight,
    drawerOpen,
    setDrawerOpen,
    isDragging,
    setIsDragging,
    selectedVariation,
    variantDetails,
    newComment,
    setNewComment,
    handleCloseDrawer,
    handleMouseDown,
    onCommentVariant,
    handleDeleteComment,
    selectedVariant,
    dataLoading,
    variantComments,
    onValidateVariant,
    onRejectVariant,
    onRemoveVariant,
    setSelectedVariation,
    setVariantDetails,
    setSelectedRows,
  } = useVariantDrawer(outputID, personStatus, currentCursor);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const windowHeight = window.innerHeight;
      const mouseY = e.clientY;
      const newHeight = ((windowHeight - mouseY) / windowHeight) * 100;

      // Constrain height between 20% and 90%
      const constrainedHeight = Math.max(20, Math.min(90, newHeight));
      setDrawerHeight(constrainedHeight);
    },
    [isDragging, setDrawerHeight],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }

    // Return a no-op function to satisfy ESLint
    return () => {};
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Sync cursor to currentCursor
  useEffect(() => {
    if (shouldFetch) {
      setCurrentCursor(cursor);
      setShouldFetch(false);
    }
  }, [cursor, shouldFetch]);

  useEffect(() => {
    if (currentCursor) {
      /* empty */
    }
  }, [currentCursor]);

  useEffect(() => {
    if (!selectedVariant) return;

    const { columsOutput, rowsOutput, cursorOutput, rowCountOutput } =
      processOutputData(selectedVariant);

    // Check if reporting_variant already exists
    const hasReportingColumn = columsOutput.some(
      (col) => col.field === 'reporting_variant',
    );

    let finalColumns = [...columsOutput];

    if (!hasReportingColumn) {
      finalColumns = [
        ...finalColumns,
        {
          field: 'reporting_variant',
          headerName: 'Reporting',
          flex: 1,
          editable: true,
          renderEditCell: (params) => (
            <Select
              value={params.value || ''}
              onChange={(e) =>
                params.api.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: e.target.value,
                })
              }
              size="small"
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="primary">Primary</MenuItem>
              <MenuItem value="secondary">Secondary</MenuItem>
              <MenuItem value="incidental">Incidental</MenuItem>
            </Select>
          ),
        },
      ];
    }

    const hideColumns = new Set(['id', 'selected_variant_id']);
    const excludeColumns = new Set(['']);

    const filteredColumns = finalColumns
      // First: completely remove excluded columns
      .filter((col) => !hideColumns.has(col.field))
      .filter((col) => !excludeColumns.has(col.field));

    setColumns(filteredColumns);
    setRowData(rowsOutput);
    setRowCount(rowCountOutput);

    setCursor(cursorOutput);
  }, [selectedVariant]);

  const extractCursor = (url: string | null): string | null => {
    if (!url) return null;

    try {
      const parsedUrl = new URL(url);
      return parsedUrl.searchParams.get('cursor');
    } catch (error) {
      return null;
    }
  };

  const handlePaginationModelChange = (model: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationModel(model);

    const newCursor =
      model.page > paginationModel.page
        ? extractCursor(selectedVariant?.next)
        : extractCursor(selectedVariant?.previous);

    if (newCursor) {
      setCursor(newCursor);
      setShouldFetch(true);
    }
  };
  // General and per-variation comment state
  const [generalComments, setGeneralComments] = useState<VariantComment[]>([]);

  const [newGeneralComment, setNewGeneralComment] = useState('');

  // Add general comment
  const handleAddGeneralComment = () => {
    if (!newGeneralComment.trim()) return;

    const newEntry: VariantComment = {
      id: Date.now().toString(), // must be string
      created_by_email: 'Current User',
      created_at: new Date().toISOString(), // match server format
      comment: newGeneralComment.trim(),
      comment_type: 'general', // or 'first_validation', 'rejection'
    };

    setGeneralComments((prev) => [newEntry, ...prev]);
    setNewGeneralComment('');
  };

  // Delete
  const handleDeleteGeneralComment = (id: string) => {
    setGeneralComments((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <Stack direction="column" spacing={4} sx={{ px: 3, py: 4 }}>
      {/* DataGrid Section */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Selected Variant ({personStatus})
        </Typography>

        {/* Right-aligned button */}
        <Box display="flex" justifyContent="flex-start" mb={2}>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={onRemoveVariant}
          >
            Remove variants
          </Button>
        </Box>

        {/* DataGrid */}
        <DataGrid
          autoHeight
          paginationMode="server"
          rows={rowData}
          columns={columns}
          loading={dataLoading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          // onRowClick={handleRowClick}
          onCellClick={(params) => {
            const blocked = ['selection', 'reporting_variant', 'actions']; // your interactive fields
            if (blocked.includes(params.field)) return; // do nothing

            // otherwise open
            const variation = params.row.uploaded_variation;
            setSelectedVariation(variation);
            setVariantDetails(params.row);
            setDrawerOpen(true);
          }}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          checkboxSelection
          keepNonExistentRowsSelected
          sx={{
            '& .MuiDataGrid-row': { cursor: 'pointer' },
          }}
          onRowSelectionModelChange={(newSelection) =>
            setSelectedRows(newSelection)
          }
        />
      </Box>

      {/* General Comments (no height limit) */}
      <Box>
        <Typography variant="h6" gutterBottom>
          General Comments
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="Write a general comment..."
            variant="outlined"
            multiline
            minRows={3}
            fullWidth
            value={newGeneralComment}
            onChange={(e) => setNewGeneralComment(e.target.value)}
          />
          <Box textAlign="right">
            <Button
              variant="contained"
              onClick={handleAddGeneralComment}
              disabled={!newGeneralComment.trim()}
            >
              Submit Comment
            </Button>
          </Box>
        </Stack>

        <Box mt={4}>
          {generalComments.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={2}>
              No general comments yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {generalComments.map((comment) => (
                <Paper key={comment.id} variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography fontWeight="bold">
                        {comment.comment}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {comment.created_at}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteGeneralComment(comment.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                    {comment.comment}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {/* Bottom Drawer for Comments */}
      <VariantDetailsPage
        drawerHeight={drawerHeight}
        handleCloseDrawer={handleCloseDrawer}
        drawerOpen={drawerOpen}
        isDragging={isDragging}
        handleMouseDown={handleMouseDown}
        variantDetails={variantDetails}
        selectedVariation={selectedVariation}
        newComment={newComment}
        handleDeleteComment={handleDeleteComment}
        setNewComment={setNewComment}
        allowValidateVariant={allowValidateVariant}
        allowRejectVariant={allowRejectVariant}
        onValidateVariant={onValidateVariant}
        onRejectVariant={onRejectVariant}
        onCommentVariant={onCommentVariant}
        variantComments={variantComments}
      />
    </Stack>
  );
};

export default InterpretationPage;
